import React, { useEffect } from 'react';
import ReactDOM from 'react-dom';
import { useTranslation } from 'react-i18next';
import '../scss/confirm-delete-modal.scss';

/* ============================================================================
   ConfirmDeleteModal — destructive-action confirmation dialog.

   Rewritten to:
     1. Portal into <body>. Previously rendered inline as a child of whatever
        component opened it — so any ancestor with a `transform` (page
        entrance animations, hero cards, motion wrappers) turned the
        backdrop's `position: fixed` into a "fixed relative to the
        transformed ancestor" — which pinned the modal to the table area
        instead of the viewport. Portaling to <body> escapes every
        containing-block quirk and centers the modal on the actual screen.

     2. Fix a Rules-of-Hooks bug — the previous version called
        `useTranslation()` AFTER `if (!open) return null`, so the hook
        count changed when the modal opened. React logs
        "Rendered more hooks than during the previous render." The hook
        now sits above the early return.

     3. Re-theme to the brand violet palette (#7c3aed / #a855f7) — the
        previous look used a lone #5A45A5 that no longer matches the rest
        of the dashboard. Backdrop is frosted violet, primary action is
        the gradient CTA, cancel is a ghost-violet outline.

     4. Accessibility improvements — Esc closes (calls onCancel), backdrop
        click closes (calls onCancel). Both are disabled while `loading`
        so the user can't cancel mid-delete. Marked as a proper role="dialog"
        with aria-modal + aria-labelledby.

   The props API is identical to the previous version, so every existing
   consumer (draft-invoice-section, my-client-section, plus any future call
   site) keeps working with zero changes.
   ============================================================================ */

interface ConfirmDeleteModalProps {
    open: boolean;
    title?: string;
    description?: string;
    loading?: boolean;
    onCancel: () => void;
    onConfirm: () => void;
}

const ConfirmDeleteModal: React.FC<ConfirmDeleteModalProps> = ({
    open,
    title = 'Delete Invoice',
    description = 'Are you sure you want to delete this draft invoice?',
    loading = false,
    onCancel,
    onConfirm,
}) => {
    /* Hooks MUST come before any conditional return so React sees the same
       hook order on every render — this includes the useTranslation() call
       below plus the Esc-close useEffect. */
    const { t } = useTranslation();

    /* Esc closes the modal — respects `loading` so we don't tear it down
       mid-delete. Only bound while open to avoid holding a listener when
       the modal isn't mounted visually. */
    useEffect(() => {
        if (!open) return;
        const onKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape' && !loading) onCancel();
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [open, loading, onCancel]);

    if (!open) return null;

    /* Backdrop click closes — but ignore clicks on the card itself so a
       stray click inside the dialog doesn't dismiss it. Also gated by
       `loading` so the user can't kill the flow mid-request. */
    const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
        if (e.target === e.currentTarget && !loading) onCancel();
    };

    const content = (
        <div
            className="confirm-modal-backdrop"
            role="dialog"
            aria-modal="true"
            aria-labelledby="confirm-modal-title"
            onClick={handleBackdropClick}
        >
            <div className="confirm-modal">
                {/* Destructive-action icon — small red-tinted alert
                    triangle that visually flags this as a delete. */}
                <div className="confirm-modal__icon" aria-hidden="true">
                    <svg width="28" height="28" viewBox="0 0 24 24" fill="none">
                        <path
                            d="M12 3.75l9.5 16.5H2.5L12 3.75z"
                            fill="rgba(239, 68, 68, 0.12)"
                            stroke="#ef4444"
                            strokeWidth="1.8"
                            strokeLinejoin="round"
                        />
                        <path d="M12 10v4" stroke="#ef4444" strokeWidth="2" strokeLinecap="round" />
                        <circle cx="12" cy="17" r="1.1" fill="#ef4444" />
                    </svg>
                </div>

                <h3 id="confirm-modal-title" className="confirm-modal__title">{title}</h3>
                <p className="confirm-modal__body">{description}</p>

                <div className="confirm-modal-actions">
                    <button
                        type="button"
                        className="btn-cancel"
                        onClick={onCancel}
                        disabled={loading}
                    >
                        {t('CANCEL_CONFIRM')}
                    </button>

                    <button
                        type="button"
                        className="btn-delete"
                        onClick={onConfirm}
                        disabled={loading}
                    >
                        {t('DELETE_CONFIRM')}
                    </button>
                </div>
            </div>
        </div>
    );

    /* Portal to <body> so the backdrop's fixed positioning is always
       viewport-relative, regardless of any transformed ancestor. */
    return ReactDOM.createPortal(content, document.body);
};

export default ConfirmDeleteModal;
